import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import CosmicBackground from '../components/CosmicBackground';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen relative overflow-hidden">
      <CosmicBackground />
      
      <div className="relative z-10 min-h-screen">
        <header className="glass-deep px-6 py-4 border-b border-white/5">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl text-starlight">Privacy Policy</h1>
          </div>
        </header>
        
        <div className="p-6 max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-8 space-y-6 text-starlight/80">
            <p className="text-starlight/50 text-sm">Last Updated: {new Date().toLocaleDateString()}</p>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Introduction</h2>
              <p>
                Welcome to Alvin's Place ("we," "our," or "us"). We are committed to protecting your personal 
                information and your right to privacy. This Privacy Policy explains what information we collect, 
                how we use it, and your rights regarding your data.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Information We Collect</h2>
              <p className="mb-3">We collect the following information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Account Information:</strong> Name, email address, and profile picture (via Google OAuth)</li>
                <li><strong>API Keys:</strong> Your Anthropic API key (encrypted and stored securely)</li>
                <li><strong>Content:</strong> Conversation history, companion vessel data, and uploaded memory files</li>
                <li><strong>Usage Data:</strong> How you interact with the platform, features used, and timestamps</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>To provide and maintain the service</li>
                <li>To authenticate your account via Google OAuth</li>
                <li>To enable conversations with AI companions using your API key</li>
                <li>To store and retrieve your companion vessel data and memories</li>
                <li>To improve and optimize the platform</li>
                <li>To communicate with you about updates or issues</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Data Security</h2>
              <p>
                We take security seriously. Your API keys are encrypted using industry-standard encryption 
                (Fernet) before storage. Your conversation data is stored securely in our database with access 
                controls. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Third-Party Services</h2>
              <p className="mb-2">We use the following third-party services:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Google OAuth:</strong> For authentication (subject to Google's Privacy Policy)</li>
                <li><strong>Anthropic Claude:</strong> For AI conversations (using your personal API key)</li>
                <li><strong>MongoDB:</strong> For data storage</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Your Rights</h2>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Export your data</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Data Retention</h2>
              <p>
                We retain your data as long as your account is active. If you delete your account, we will 
                delete your personal information within 30 days, except where required by law.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Cookies</h2>
              <p>
                We use essential cookies to maintain your session and authentication state. These are necessary 
                for the platform to function properly.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Children's Privacy</h2>
              <p>
                Our service is not intended for users under the age of 13. We do not knowingly collect 
                information from children under 13.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or want to exercise your rights, please contact us at:
              </p>
              <p className="mt-2">
                <a 
                  href="https://patreon.com/story_of_everything"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-etheric hover:underline"
                >
                  Via Patreon Message
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
